import { useEffect, useState } from "react";
import { Globe } from "lucide-react";

const LANGS = [
  { code: "en", label: "English", short: "EN" },
  { code: "kn", label: "ಕನ್ನಡ", short: "KN" },
  { code: "hi", label: "हिन्दी", short: "HI" },
];

function readCookieLang() {
  const m = document.cookie.match(/googtrans=\/[a-z]+\/([a-z]+)/);
  return m ? m[1] : "en";
}

function writeCookieLang(code) {
  const host = window.location.hostname;
  const value = `/en/${code}`;
  // clear on all likely scopes then set fresh
  const scopes = ["", `;domain=${host}`, `;domain=.${host}`];
  const rootParts = host.split(".");
  if (rootParts.length > 1) {
    scopes.push(`;domain=${rootParts.slice(-2).join(".")}`);
    scopes.push(`;domain=.${rootParts.slice(-2).join(".")}`);
  }
  scopes.forEach((s) => {
    document.cookie = `googtrans=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${s}`;
  });
  scopes.forEach((s) => {
    document.cookie = `googtrans=${value};path=/${s}`;
  });
}

function pickSelect() {
  return document.querySelector("select.goog-te-combo");
}

function applyViaSelect(code) {
  const sel = pickSelect();
  if (!sel) return false;
  sel.value = code === "en" ? "" : code;
  sel.dispatchEvent(new Event("change", { bubbles: true }));
  return true;
}

export default function LanguageSwitcher({ variant = "dark" }) {
  const [current, setCurrent] = useState("en");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setCurrent(readCookieLang());
  }, []);

  const change = (code) => {
    if (code === current) { setOpen(false); return; }
    setBusy(true);
    setOpen(false);
    writeCookieLang(code);
    // Try live-swap first
    const tryApply = (attempts) => {
      if (applyViaSelect(code)) {
        setCurrent(code);
        setBusy(false);
        return;
      }
      if (attempts <= 0) {
        // fallback: reload — Google Translate picks up the cookie
        window.location.reload();
        return;
      }
      setTimeout(() => tryApply(attempts - 1), 300);
    };
    tryApply(20);
  };

  const active = LANGS.find((l) => l.code === current) || LANGS[0];
  const isDark = variant === "dark";
  const toneBtn = isDark
    ? "text-white/85 hover:text-teal border-white/20"
    : "text-navy hover:text-teal border-navy/20";
  const menuTone = isDark ? "bg-navy-deep text-white border-white/10" : "bg-white text-navy border-navy/10";

  return (
    <div className="relative notranslate" translate="no" data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className={`inline-flex items-center gap-2 px-3 py-1.5 border rounded-sm text-xs font-display font-bold uppercase tracking-widest transition-colors ${toneBtn} disabled:opacity-60`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change language"
      >
        <Globe className="w-3.5 h-3.5" />
        <span>{busy ? "…" : active.short}</span>
        <span className="text-teal">▾</span>
      </button>
      {open && (
        <ul
          role="listbox"
          className={`absolute right-0 top-full mt-1 min-w-[170px] border rounded-sm shadow-xl z-[60] ${menuTone}`}
        >
          {LANGS.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                data-testid={`lang-option-${l.code}`}
                onClick={() => change(l.code)}
                className={`w-full text-left px-4 py-2 text-sm font-display transition-colors ${
                  current === l.code
                    ? "bg-teal text-white"
                    : isDark
                    ? "hover:bg-white/10"
                    : "hover:bg-teal/10"
                }`}
              >
                <span className="font-bold mr-2">{l.short}</span>
                <span className="opacity-80">{l.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* Google Translate mount point — hidden but functional */}
      <div id="google_translate_element" aria-hidden style={{ position: "absolute", left: "-9999px", top: "-9999px" }} />
    </div>
  );
}
